/**
 * PdfPane — fetches a signed PDF URL into memory and renders it with the
 * existing CRM PdfViewer (react-pdf, lazy chunk). Read-only.
 */
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Box, Center, Spinner, Text, Button } from '@chakra-ui/react';

const PdfViewer = lazy(() => import('../CRM/Documents/viewers/PdfViewer'));

export default function PdfPane({ url, fileName = 'document.pdf' }) {
  const [buffer, setBuffer] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url) return undefined;
    let alive = true;
    setBuffer(null);
    setError(null);
    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`fetch failed (${res.status})`);
        const bytes = await res.arrayBuffer();
        if (alive) setBuffer(bytes);
      } catch (err) {
        if (alive) setError(err.message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);

  if (!url) {
    return (
      <Center h="100%" minH="300px">
        <Text fontSize="sm" color="gray.500">
          No PDF available.
        </Text>
      </Center>
    );
  }
  if (error) {
    return (
      <Center h="100%" minH="300px" flexDirection="column">
        <Text fontSize="sm" color="red.400" mb={2}>
          Could not load PDF: {error}
        </Text>
        <Button size="sm" as="a" href={url} target="_blank" rel="noopener noreferrer">
          Open signed URL
        </Button>
      </Center>
    );
  }
  if (!buffer) {
    return (
      <Center h="100%" minH="300px">
        <Spinner color="blue.400" />
      </Center>
    );
  }
  return (
    <Box h="100%" minH={0} display="flex" flexDirection="column">
      <Suspense
        fallback={
          <Center h="100%" minH="300px">
            <Spinner color="blue.400" />
          </Center>
        }
      >
        <PdfViewer buffer={buffer} fileName={fileName} />
      </Suspense>
    </Box>
  );
}
